import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import config from '../config';

const API = config.API_URL;

const PART_CATEGORIES = [
  { value: 'screen', label: 'Screen' },
  { value: 'battery', label: 'Battery' },
  { value: 'charging_port', label: 'Charging port' },
  { value: 'speaker', label: 'Speaker' },
  { value: 'camera', label: 'Camera' },
  { value: 'back_glass', label: 'Back glass' },
  { value: 'flex_cable', label: 'Flex cable' },
  { value: 'other', label: 'Other' },
];

function inr(n) {
  const v = Number(n);
  if (!v && v !== 0) return '—';
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

function RepairBusinessAnalysis() {
  const [months, setMonths] = useState(6);
  const [analytics, setAnalytics] = useState(null);
  const [partsData, setPartsData] = useState({ parts: [], lowStockCount: 0, stockValue: 0 });
  const [tab, setTab] = useState('profit');
  const [loading, setLoading] = useState(true);
  const [partForm, setPartForm] = useState({
    name: '', category: 'screen', compatibleModels: '', quantity: '', unitCost: '', lowStockAt: '2',
  });
  const [savingPart, setSavingPart] = useState(false);
  const [laborTiers, setLaborTiers] = useState([]);
  const [savingLabor, setSavingLabor] = useState(false);
  const [partsSearch, setPartsSearch] = useState('');
  const [selectedPart, setSelectedPart] = useState(null);
  const [partEditForm, setPartEditForm] = useState({ appendModels: '', unitCost: '', lowStockAt: '' });
  const [savingPartEdit, setSavingPartEdit] = useState(false);

  const loadLabor = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/repair-labor`);
      setLaborTiers(res.data.tiers || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/repairs/analytics?months=${months}`);
      setAnalytics(res.data);
    } catch (e) {
      console.error(e);
    }
  }, [months]);

  const loadParts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/repair-parts`);
      setPartsData(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadAnalytics(), loadParts(), loadLabor()]);
    setLoading(false);
  }, [loadAnalytics, loadParts, loadLabor]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const addPart = async (e) => {
    e.preventDefault();
    if (!partForm.name.trim()) return;
    setSavingPart(true);
    try {
      await axios.post(`${API}/repair-parts`, partForm);
      setPartForm({ name: '', category: 'screen', compatibleModels: '', quantity: '', unitCost: '', lowStockAt: '2' });
      await loadParts();
    } catch (err) {
      alert(err?.response?.data?.message || 'Save failed');
    } finally {
      setSavingPart(false);
    }
  };

  const adjustQty = async (id, delta) => {
    try {
      await axios.patch(`${API}/repair-parts/${id}`, { adjustQty: delta });
      loadParts();
    } catch (err) {
      alert(err?.response?.data?.message || 'Update failed');
    }
  };

  const openPartEdit = (part) => {
    setSelectedPart(part);
    setPartEditForm({
      appendModels: '',
      unitCost: String(part.unitCost ?? ''),
      lowStockAt: String(part.lowStockAt ?? 2),
    });
  };

  const closePartEdit = () => {
    setSelectedPart(null);
    setPartEditForm({ appendModels: '', unitCost: '', lowStockAt: '' });
  };

  const savePartEdit = async () => {
    if (!selectedPart) return;
    setSavingPartEdit(true);
    try {
      const payload = {
        unitCost: Number(partEditForm.unitCost) || 0,
        lowStockAt: Number(partEditForm.lowStockAt) ?? 2,
      };
      if (partEditForm.appendModels.trim()) {
        payload.appendCompatibleModels = partEditForm.appendModels.trim();
      }
      await axios.patch(`${API}/repair-parts/${selectedPart._id}`, payload);
      closePartEdit();
      await loadParts();
    } catch (err) {
      alert(err?.response?.data?.message || 'Save failed');
    } finally {
      setSavingPartEdit(false);
    }
  };

  const filteredParts = useMemo(() => {
    const list = partsData.parts || [];
    const q = partsSearch.trim().toLowerCase();
    if (!q) return list;
    return list
      .map((p) => {
        const hay = `${p.name} ${p.compatibleModels} ${p.category} ${p.sku || ''}`.toLowerCase();
        const modelMatch = String(p.compatibleModels || '').toLowerCase().includes(q);
        const nameMatch = String(p.name || '').toLowerCase().includes(q);
        const score = (modelMatch ? 10 : 0) + (nameMatch ? 5 : 0) + (hay.includes(q) ? 1 : 0);
        return { ...p, _searchScore: score };
      })
      .filter((p) => p._searchScore > 0)
      .sort((a, b) => b._searchScore - a._searchScore);
  }, [partsData.parts, partsSearch]);

  const saveLaborRates = async () => {
    setSavingLabor(true);
    try {
      const tierRates = {};
      laborTiers.forEach((t) => {
        tierRates[t.tier] = {};
        (t.rates || []).forEach((r) => {
          tierRates[t.tier][r.key] = r.laborCost;
        });
      });
      await axios.put(`${API}/repair-labor`, { tierRates });
      await loadLabor();
      alert('Labour rates save ho gayi');
    } catch (err) {
      alert(err?.response?.data?.message || 'Save failed');
    } finally {
      setSavingLabor(false);
    }
  };

  const updateTierRate = (tierIdx, rateIdx, value) => {
    setLaborTiers((prev) => {
      const next = prev.map((t, ti) => {
        if (ti !== tierIdx) return t;
        return {
          ...t,
          rates: t.rates.map((r, ri) => (ri === rateIdx ? { ...r, laborCost: Number(value) || 0 } : r)),
        };
      });
      return next;
    });
  };

  const t = analytics?.totals || {};

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-0"><i className="bi bi-tools me-2" />Repair Business Analysis</h4>
          <small className="text-muted">Punjab PB001 — profit = customer charge − parts cost − labour (admin only)</small>
        </div>
        <div className="d-flex gap-2">
          <select className="form-select form-select-sm" style={{ width: 130 }} value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
          </select>
          <Link to="/repairs" className="btn btn-sm btn-primary">Repair counter</Link>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'profit' ? 'active' : ''}`} onClick={() => setTab('profit')}>Profit & trends</button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'labor' ? 'active' : ''}`} onClick={() => setTab('labor')}>Labour rates</button>
        </li>
        <li className="nav-item">
          <button type="button" className={`nav-link ${tab === 'parts' ? 'active' : ''}`} onClick={() => setTab('parts')}>
            Parts inventory
            {partsData.lowStockCount > 0 && <span className="badge bg-warning text-dark ms-1">{partsData.lowStockCount} low</span>}
          </button>
        </li>
      </ul>

      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : tab === 'profit' ? (
        <>
          <div className="row g-2 mb-3">
            {[
              { label: 'Delivered jobs', value: t.jobs ?? 0, sub: `${months} months` },
              { label: 'Revenue', value: inr(t.revenue), sub: 'customer paid' },
              { label: 'Parts cost', value: inr(t.partsCost), sub: 'inventory auto' },
              { label: 'Labour (repairman)', value: inr(t.laborCost), sub: 'fixed per problem' },
              { label: 'Gross profit', value: inr(t.profit), sub: `${analytics?.marginPct ?? 0}% margin`, highlight: true },
              { label: 'Avg ticket', value: inr(analytics?.avgTicket), sub: 'per job' },
            ].map((c) => (
              <div key={c.label} className="col-6 col-md-4 col-lg">
                <div className={`card h-100 ${c.highlight ? 'border-success' : ''}`}>
                  <div className="card-body py-2">
                    <div className="text-muted small">{c.label}</div>
                    <div className="fs-5 fw-bold">{c.value}</div>
                    <small className="text-muted">{c.sub}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="alert alert-info py-2 small">
            <strong>Automatic flow:</strong> Phone type (iPhone / budget / premium) + problem se labour auto.
            <strong> Other</strong> par finish time manual labour. Parts model se suggest hote hain. Profit yahi page par.
          </div>

          <div className="row g-3">
            <div className="col-lg-7">
              <div className="card">
                <div className="card-header py-2"><strong>Monthly breakdown</strong></div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Month</th><th>Jobs</th><th>Revenue</th><th>Parts</th><th>Labour</th><th>Profit</th></tr></thead>
                    <tbody>
                      {(analytics?.monthly || []).map((m) => (
                        <tr key={m.month}>
                          <td>{m.month}</td>
                          <td>{m.jobs}</td>
                          <td>{inr(m.revenue)}</td>
                          <td>{inr(m.partsCost)}</td>
                          <td>{inr(m.laborCost)}</td>
                          <td className={m.profit > 0 ? 'text-success fw-semibold' : ''}>{inr(m.profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="card">
                <div className="card-header py-2"><strong>Revenue by problem type</strong></div>
                <ul className="list-group list-group-flush">
                  {(analytics?.byIssue || []).slice(0, 8).map((x) => (
                    <li key={x.category} className="list-group-item d-flex justify-content-between py-2">
                      <span>{x.category} <small className="text-muted">({x.count})</small></span>
                      <span>{inr(x.revenue)}</span>
                    </li>
                  ))}
                  {!analytics?.byIssue?.length && <li className="list-group-item text-muted">Abhi delivered jobs nahi</li>}
                </ul>
              </div>
              <div className="card mt-2">
                <div className="card-header py-2"><strong>Top models repaired</strong></div>
                <ul className="list-group list-group-flush">
                  {(analytics?.byModel || []).slice(0, 10).map((x) => (
                    <li key={x.modelKey || x.model} className="list-group-item d-flex justify-content-between py-2">
                      <span>{x.model} <small className="text-muted">({x.count})</small></span>
                      <span>{inr(x.revenue)}</span>
                    </li>
                  ))}
                  {!analytics?.byModel?.length && <li className="list-group-item text-muted">Delivered jobs ke baad yahan dikhega</li>}
                </ul>
              </div>
              <div className="card mt-2">
                <div className="card-header py-2"><strong>Most used parts</strong></div>
                <ul className="list-group list-group-flush">
                  {(analytics?.topPartsUsed || []).slice(0, 8).map((x) => (
                    <li key={x.partName} className="list-group-item d-flex justify-content-between py-2">
                      <span>{x.partName} <small className="text-muted">×{x.quantity}</small></span>
                      <span className="text-muted">{x.jobs} jobs</span>
                    </li>
                  ))}
                  {!analytics?.topPartsUsed?.length && <li className="list-group-item text-muted">Parts use data yahan aayega</li>}
                </ul>
              </div>
              <div className="card mt-2">
                <div className="card-body py-2 small text-muted">
                  Inventory stock value (at cost): <strong>{inr(partsData.stockValue)}</strong>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : tab === 'labor' ? (
        <div className="card">
          <div className="card-header py-2 d-flex justify-content-between align-items-center">
            <strong>Labour rates — phone type × problem (repairman cost)</strong>
            <button type="button" className="btn btn-sm btn-primary" onClick={saveLaborRates} disabled={savingLabor}>
              {savingLabor ? 'Saving…' : 'Save all rates'}
            </button>
          </div>
          <div className="card-body">
            <p className="small text-muted mb-3">
              iPhone alag, budget/keypad alag, premium alag. Problem <strong>Other</strong> par finish time manual labour likhoge.
            </p>
            {laborTiers.map((tier, tierIdx) => (
              <div key={tier.tier} className="mb-4">
                <h6 className="border-bottom pb-1">{tier.label}</h6>
                <div className="row g-2">
                  {(tier.rates || []).map((r, rateIdx) => (
                    <div key={r.key} className="col-6 col-md-4 col-lg-3">
                      <label className="form-label small mb-0">{r.label}</label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">₹</span>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          value={r.laborCost}
                          onChange={(e) => updateTierRate(tierIdx, rateIdx, e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header py-2"><strong>Add part (shop stock)</strong></div>
              <div className="card-body">
                <form onSubmit={addPart}>
                  <div className="mb-2">
                    <label className="form-label small mb-0">Name *</label>
                    <input className="form-control form-control-sm" placeholder="iPhone 11 screen" value={partForm.name} onChange={(e) => setPartForm((f) => ({ ...f, name: e.target.value }))} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-0">Category</label>
                    <select className="form-select form-select-sm" value={partForm.category} onChange={(e) => setPartForm((f) => ({ ...f, category: e.target.value }))}>
                      {PART_CATEGORIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-0">Compatible models</label>
                    <input className="form-control form-control-sm" placeholder="iPhone 11, 11 Pro…" value={partForm.compatibleModels} onChange={(e) => setPartForm((f) => ({ ...f, compatibleModels: e.target.value }))} />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-4">
                      <label className="form-label small mb-0">Qty</label>
                      <input type="number" min="0" className="form-control form-control-sm" value={partForm.quantity} onChange={(e) => setPartForm((f) => ({ ...f, quantity: e.target.value }))} />
                    </div>
                    <div className="col-4">
                      <label className="form-label small mb-0">Cost ₹</label>
                      <input type="number" min="0" className="form-control form-control-sm" value={partForm.unitCost} onChange={(e) => setPartForm((f) => ({ ...f, unitCost: e.target.value }))} />
                    </div>
                    <div className="col-4">
                      <label className="form-label small mb-0">Low at</label>
                      <input type="number" min="0" className="form-control form-control-sm" value={partForm.lowStockAt} onChange={(e) => setPartForm((f) => ({ ...f, lowStockAt: e.target.value }))} />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm w-100" disabled={savingPart}>{savingPart ? 'Saving…' : 'Add to inventory'}</button>
                </form>
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header py-2">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <strong>Parts list</strong>
                  <span className="small text-muted">{partsData.parts?.length || 0} items · {inr(partsData.stockValue)} at cost</span>
                </div>
                <div className="input-group input-group-sm">
                  <span className="input-group-text"><i className="bi bi-search" /></span>
                  <input
                    className="form-control"
                    placeholder="Model search — e.g. iPhone 11, Y20, M6 Plus…"
                    value={partsSearch}
                    onChange={(e) => setPartsSearch(e.target.value)}
                  />
                  {partsSearch && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setPartsSearch('')}>Clear</button>
                  )}
                </div>
                {partsSearch && (
                  <div className="small text-muted mt-1">
                    {filteredParts.length} match — green = stock hai, red = out of stock
                  </div>
                )}
                <div className="small text-muted mt-1">Row pe click karo — models aur price edit</div>
              </div>
              <div className="table-responsive" style={{ maxHeight: 480, overflowY: 'auto' }}>
                <table className="table table-sm table-hover mb-0 repair-parts-table">
                  <thead className="table-light sticky-top">
                    <tr><th>Part</th><th>Compatible models</th><th>Stock</th><th>Cost</th><th></th></tr>
                  </thead>
                  <tbody>
                    {filteredParts.map((p) => (
                      <tr
                        key={p._id}
                        className={`repair-part-row ${p.quantity <= (p.lowStockAt ?? 2) ? 'table-warning' : ''} ${selectedPart?._id === p._id ? 'table-active' : ''}`}
                        onClick={() => openPartEdit(p)}
                        title="Click to edit models & price"
                      >
                        <td>
                          {p.name}<br /><small className="text-muted">{p.category}</small>
                          {partsSearch && p.quantity > 0 && <span className="badge bg-success ms-1">Available</span>}
                          {partsSearch && p.quantity < 1 && <span className="badge bg-danger ms-1">Out</span>}
                        </td>
                        <td className="small text-truncate" style={{ maxWidth: 300 }} title={p.compatibleModels || ''}>
                          {p.compatibleModels || '—'}
                        </td>
                        <td><strong className={p.quantity < 1 ? 'text-danger' : 'text-success'}>{p.quantity}</strong></td>
                        <td>{inr(p.unitCost)}</td>
                        <td className="text-nowrap" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="btn btn-outline-secondary btn-sm py-0" onClick={() => adjustQty(p._id, -1)} aria-label="Decrease stock">−</button>
                          <button type="button" className="btn btn-outline-secondary btn-sm py-0 ms-1" onClick={() => adjustQty(p._id, 1)} aria-label="Increase stock">+</button>
                          <button type="button" className="btn btn-link btn-sm py-0 ms-1 text-muted" onClick={() => openPartEdit(p)} aria-label="Edit part">
                            <i className="bi bi-pencil-square" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!filteredParts.length && (
                      <tr><td colSpan={5} className="text-muted text-center py-4">
                        {partsSearch ? `"${partsSearch}" ke liye koi part nahi mila` : 'Pehla part add karo — screens, batteries, ports…'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPart && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={closePartEdit}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header py-2">
                <div>
                  <h5 className="modal-title mb-0">{selectedPart.name}</h5>
                  <small className="text-muted text-capitalize">{selectedPart.category} · Stock: {selectedPart.quantity}</small>
                </div>
                <button type="button" className="btn-close" onClick={closePartEdit} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small fw-semibold mb-1">Compatible models (current)</label>
                  <div className="border rounded bg-light px-2 py-2 small" style={{ maxHeight: 100, overflowY: 'auto' }}>
                    {selectedPart.compatibleModels || <span className="text-muted">Abhi koi model nahi</span>}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold mb-1">Naye models add karo</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Y21, Y22, T2x 5g… (comma se alag)"
                    value={partEditForm.appendModels}
                    onChange={(e) => setPartEditForm((f) => ({ ...f, appendModels: e.target.value }))}
                  />
                  <div className="form-text">Purane models safe rahenge — sirf naye merge honge</div>
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small fw-semibold mb-1">Cost price ₹</label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={partEditForm.unitCost}
                        onChange={(e) => setPartEditForm((f) => ({ ...f, unitCost: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold mb-1">Low stock alert at</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-sm"
                      value={partEditForm.lowStockAt}
                      onChange={(e) => setPartEditForm((f) => ({ ...f, lowStockAt: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer py-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closePartEdit}>Cancel</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={savePartEdit} disabled={savingPartEdit}>
                  {savingPartEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RepairBusinessAnalysis;
